import type Keycloak from "keycloak-js";
import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./schema";
import { getEnvVariable } from "#/utils/env";

const baseUrl = new URL(getEnvVariable("VITE_BACKEND_ENTRYPOINT_URL")).origin;

export const apiClient = createClient<paths, "application/ld+json">({
  baseUrl,
});

/**
 * Auth middleware — refreshes the token proactively (if it expires within 30 s)
 * then attaches it to every outgoing request.
 */
let keycloakInstance: Keycloak | undefined;

export const setKeycloak = (kc: Keycloak) => {
  keycloakInstance = kc;
};

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    if (keycloakInstance?.authenticated) {
      await keycloakInstance.updateToken(30);
      request.headers.set("Authorization", `Bearer ${keycloakInstance.token}`);
    }
    return request;
  },
};

/**
 * Tenant middleware — injects tenantId query parameter.
 */
let currentTenantId: number | undefined;

export const setTenantId = (tenantId: number | undefined) => {
  currentTenantId = tenantId;
};

const tenantMiddleware: Middleware = {
  async onRequest({ request }) {
    if (currentTenantId !== undefined) {
      const url = new URL(request.url);
      if (url.searchParams.has("tenantId")) {
        return request;
      }
      url.searchParams.set("tenantId", String(currentTenantId));
      const body =
        request.body !== null ? await request.clone().text() : undefined;
      return new Request(url, {
        method: request.method,
        headers: request.headers,
        body,
      });
    }
    return request;
  },
};

/**
 * JSON-LD middleware — sets Accept header so responses use Hydra/JSON-LD format.
 */
const jsonLdMiddleware: Middleware = {
  onRequest({ request }) {
    if (!request.headers.has("Accept")) {
      request.headers.set("Accept", "application/ld+json");
    }
    return request;
  },
};

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "HttpError";
  }
}

/**
 * Error middleware — throws on non-2xx responses so React Query's onError fires.
 * openapi-fetch returns errors in the `error` field without throwing by default.
 */
const throwOnErrorMiddleware: Middleware = {
  async onResponse({ response }) {
    if (!response.ok) {
      const body = await response.clone().text();
      throw new HttpError(
        response.status,
        body !== "" ? body : `HTTP ${response.status}`,
      );
    }
    return response;
  },
};

apiClient.use(authMiddleware);
apiClient.use(tenantMiddleware);
apiClient.use(jsonLdMiddleware);
apiClient.use(throwOnErrorMiddleware);

/**
 * Raw authenticated fetch for endpoints not in the OpenAPI schema (e.g. POST /parcels/search).
 * Applies auth + tenant middleware manually.
 */
export const fetchWithAuth = async (
  path: string,
  options: RequestInit = {},
  tenantId?: number,
): Promise<Response> => {
  const url = new URL(path, baseUrl);
  const effectiveTenantId = tenantId ?? currentTenantId;
  if (effectiveTenantId !== undefined) {
    url.searchParams.set("tenantId", String(effectiveTenantId));
  }

  const headers = new Headers(options.headers);
  if (keycloakInstance?.authenticated) {
    await keycloakInstance.updateToken(30);
    headers.set("Authorization", `Bearer ${keycloakInstance.token}`);
  }
  if (!(options.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/ld+json");
  }

  return fetch(url, { ...options, headers });
};
